import React, { useMemo, useState } from 'react';
import { Col, DatePicker, Row, Space, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import type { GroupAnalyticsParams } from '@/types';
import { SessionStatsCard } from './group-analytics/SessionStatsCard';
import { MatchesPerUserCard } from './group-analytics/MatchesPerUserCard';
import { PlayerGroupsCard } from './group-analytics/PlayerGroupsCard';
import { ModeMixCard } from './group-analytics/ModeMixCard';
import { CardDropsCard } from './group-analytics/CardDropsCard';
import { AiCompetitivenessCard } from './group-analytics/AiCompetitivenessCard';
import { IapLtvCard } from './group-analytics/IapLtvCard';
import { REPORT_MAX_DAYS, disabledBeyondReportCap, isWithinReportCap } from '@/utils/reportRange';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Player-group reports from the WCC4 reports request.
 *
 * One date range drives every card, and each card fetches its own report, so
 * one slow or failing query does not blank the others.
 */
export const GroupAnalyticsPanel: React.FC = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(29, 'day').startOf('day'),
    dayjs().endOf('day'),
  ]);

  // The server treats `to` as exclusive, so send the day after the last one picked.
  const params = useMemo<GroupAnalyticsParams>(
    () => ({
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].add(1, 'day').format('YYYY-MM-DD'),
    }),
    [range]
  );

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <RangePicker
          value={range}
          onChange={(v) =>
            v?.[0] && v?.[1] && isWithinReportCap(v[0], v[1]) && setRange([v[0], v[1]])
          }
          disabledDate={disabledBeyondReportCap}
          allowClear={false}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          At most {REPORT_MAX_DAYS} days
        </Text>
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <SessionStatsCard params={params} />
        </Col>
        <Col xs={24}>
          <MatchesPerUserCard params={params} />
        </Col>
        <Col xs={24}>
          <PlayerGroupsCard params={params} />
        </Col>
        <Col xs={24}>
          <ModeMixCard params={params} />
        </Col>
        <Col xs={24}>
          <CardDropsCard params={params} />
        </Col>
        <Col xs={24}>
          <AiCompetitivenessCard params={params} />
        </Col>
        <Col xs={24}>
          <IapLtvCard params={params} />
        </Col>
      </Row>

      <Text type="secondary" style={{ display: 'block', marginTop: 16, fontSize: 12 }}>
        Days are IST calendar days. Reports built on daily activity or session history only collect
        from when that tracking shipped, so earlier dates under-report rather than error.
      </Text>
    </div>
  );
};
